import { create } from 'zustand';

// Port nguyên từ app: store/PackagePickerStore.js
// Quản lý danh sách dịch vụ được chọn vào 1 gói (variant-aware + "dịch vụ thêm" ảo).

function getVariantName(variant) {
    if (!variant?.attributeValues || variant.attributeValues.length === 0) return null;
    return variant.attributeValues.map((a) => a.value).join(' - ');
}

function getVariantPrice(variant) {
    if (!variant) return 0;
    return Number(variant.salePrice > 1000 ? variant.salePrice : variant.retailPrice) || 0;
}

const usePackagePickerStore = create((set, get) => ({
    items: [],

    setItems: (items) =>
        set({
            items: (items || []).map((it, idx) => ({
                name: it.name,
                quantity: it.quantity || 1,
                sourceServiceId: it.sourceServiceId || null,
                sourceVariantId: it.sourceVariantId ?? null,
                price: it.price ?? 0,
                isVirtual: !!it.isVirtual,
                sortOrder: it.sortOrder ?? idx,
            })),
        }),

    addExistingWithVariant: (svc, variant) =>
        set((state) => {
            const variantId = variant?.id ?? null;
            const variantName = getVariantName(variant);
            const itemName = variantName ? `${svc.name} - ${variantName}` : svc.name;
            const price = variant ? getVariantPrice(variant) : Number(svc.price || 0);

            const existsIdx = state.items.findIndex(
                (it) => it.sourceServiceId === svc.id && it.sourceVariantId === variantId,
            );
            if (existsIdx !== -1) {
                return {
                    items: state.items.map((it, i) =>
                        i === existsIdx ? { ...it, quantity: it.quantity + 1 } : it,
                    ),
                };
            }
            return {
                items: [
                    ...state.items,
                    {
                        name: itemName,
                        quantity: 1,
                        sourceServiceId: svc.id,
                        sourceVariantId: variantId,
                        price,
                        isVirtual: false,
                        sortOrder: state.items.length,
                    },
                ],
            };
        }),

    addVirtual: (name, qty) =>
        set((state) => ({
            items: [
                ...state.items,
                {
                    name: name.trim(),
                    quantity: Math.max(1, parseInt(qty, 10) || 1),
                    sourceServiceId: null,
                    sourceVariantId: null,
                    price: 0,
                    isVirtual: true,
                    sortOrder: state.items.length,
                },
            ],
        })),

    removeAt: (idx) => set((state) => ({ items: state.items.filter((_, i) => i !== idx) })),

    removeBy: (sourceServiceId, sourceVariantId = null) =>
        set((state) => ({
            items: state.items.filter(
                (it) =>
                    !(it.sourceServiceId === sourceServiceId && it.sourceVariantId === sourceVariantId),
            ),
        })),

    updateQtyBy: (sourceServiceId, sourceVariantId, qty) =>
        set((state) => ({
            items: state.items.map((it) =>
                it.sourceServiceId === sourceServiceId && it.sourceVariantId === sourceVariantId
                    ? { ...it, quantity: Math.max(1, parseInt(qty, 10) || 1) }
                    : it,
            ),
        })),

    findItem: (sourceServiceId, sourceVariantId = null) =>
        get().items.find(
            (it) => it.sourceServiceId === sourceServiceId && it.sourceVariantId === sourceVariantId,
        ),

    countItemsForService: (sourceServiceId) =>
        get().items.filter((it) => it.sourceServiceId === sourceServiceId && !it.isVirtual).length,

    qtyForService: (sourceServiceId) =>
        get()
            .items.filter((it) => it.sourceServiceId === sourceServiceId && !it.isVirtual)
            .reduce((sum, it) => sum + (it.quantity || 0), 0),

    clear: () => set({ items: [] }),
}));

export { getVariantName, getVariantPrice };
export default usePackagePickerStore;
